import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { contactService, customerService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Search, Loader2 } from "lucide-react";

const EMPTY = {
  customerId: "", firstName: "", lastName: "", title: "", email: "", mobile: "", isPrimary: false,
};

export default function ContactsPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const custMap = Object.fromEntries(customers.map((c) => [c.id, c]));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await contactService.list({ q: q || undefined, size: 100 });
      setRows(d.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { customerService.list({ size: 200 }).then((d) => setCustomers(d.items || [])).catch(() => {}); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...EMPTY, ...row }); setOpen(true); };

  const save = async () => {
    if (!form.firstName) return toast.error("First name is required");
    if (!editing && !form.customerId) return toast.error("Select a customer");
    setSaving(true);
    try {
      if (editing) {
        const { firstName, lastName, title, email, mobile } = form;
        await contactService.update(editing.id, { firstName, lastName, title, email, mobile });
        toast.success("Contact updated");
      } else {
        const cust = custMap[form.customerId];
        const clean = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
        await contactService.create({ ...clean, tenantId: isSuper ? cust?.tenantId : undefined });
        toast.success("Contact created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setPrimary = async (row) => {
    try { await contactService.setPrimary(row.id); toast.success("Primary contact set"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.fullName}?`)) return;
    try { await contactService.remove(row.id); toast.success("Contact deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("CONTACT.CREATE");
  const canEdit = hasPermission("CONTACT.EDIT");
  const canDelete = hasPermission("CONTACT.DELETE");

  const columns = [
    { key: "fullName", header: "Name", render: (r) => (
      <span className="font-medium text-foreground flex items-center gap-1.5">
        {r.fullName}{r.isPrimary && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
      </span>
    ) },
    { key: "title", header: "Title", render: (r) => r.title || "—" },
    { key: "customerId", header: "Customer", render: (r) => <span className="text-muted-foreground">{custMap[r.customerId]?.customerName || "—"}</span> },
    { key: "email", header: "Email", render: (r) => <span className="text-muted-foreground text-xs">{r.email || "—"}</span> },
    { key: "mobile", header: "Mobile", render: (r) => r.mobile || "—" },
    {
      key: "_actions", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          {canEdit && !r.isPrimary && <button onClick={() => setPrimary(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" title="Set primary" data-testid={`primary-${r.id}`}><Star className="h-4 w-4" /></button>}
          {canEdit && <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`edit-${r.id}`}><Pencil className="h-4 w-4" /></button>}
          {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="contacts-page">
      <PageHeader
        title="Contacts"
        subtitle="Contact master — people linked to your customers"
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="contact-create-btn">
            <Plus className="h-4 w-4" /> New Contact
          </button>
        )}
      />
      <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 w-64 mb-4">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email…" className="bg-transparent text-sm outline-none w-full text-foreground" data-testid="contact-search" />
      </div>

      <DataTable columns={columns} rows={rows} loading={loading} testid="contacts-table" empty="No contacts yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="contact-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit" : "New"} Contact</h3>
            <div className="space-y-3.5">
              {!editing && (
                <div>
                  <label className="block text-sm font-medium mb-1">Customer *</label>
                  <select value={form.customerId} onChange={(e) => setForm({ ...form, customerId: e.target.value })} className="ciq-input" data-testid="contact-customer">
                    <option value="">Select customer…</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.customerName}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">First Name *</label><input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="ciq-input" data-testid="contact-first" /></div>
                <div><label className="block text-sm font-medium mb-1">Last Name</label><input value={form.lastName || ""} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="ciq-input" data-testid="contact-last" /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Title</label><input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="ciq-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Email</label><input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ciq-input" data-testid="contact-email" /></div>
                <div><label className="block text-sm font-medium mb-1">Mobile</label><input value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="ciq-input" /></div>
              </div>
              {!editing && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.isPrimary} onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })} data-testid="contact-primary" />
                  Set as primary contact
                </label>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted" data-testid="contact-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="contact-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
